import { z } from "zod";
import type { StateCreator } from "zustand/vanilla";

/**
 * segmentSplitPickSchema is the segment a split is being picked on: the itinerary
 * that scopes the write and the segment the click will be projected onto. Zod-first
 * because the value is shared with the split-pick tool input and validated when the
 * pick is armed; a null pick (nothing being split) lives outside the schema as the
 * absence of a value.
 */
export const segmentSplitPickSchema = z.object({
  itineraryId: z.string(),
  segmentId: z.string(),
});

/**
 * SegmentSplitPick is the segment a split point is being picked on, derived from
 * {@link segmentSplitPickSchema}. The split-pick slot holds this or null (no pick
 * is armed).
 */
export type SegmentSplitPick = z.infer<typeof segmentSplitPickSchema>;

/**
 * SegmentSplitSliceState is the armed split-point pick.
 */
export interface SegmentSplitSliceState {
  /**
   * segmentSplitPick names the segment a split point is currently being picked on
   * (null = no pick armed). It is what turns a map click into a split: the pick
   * tool arms it, the map surface renders its cross cursor and follow tooltip off
   * it, and the click handler resolves the clicked coordinate against THIS
   * segment's line. Set only through the tool bus, like every other UiAction.
   */
  segmentSplitPick: SegmentSplitPick | null;
}

/**
 * SegmentSplitSliceActions is the whole-slot setter that arms and disarms the
 * split-point pick, reached only through the tool bus.
 */
export interface SegmentSplitSliceActions {
  /**
   * setSegmentSplitPick arms the split-point pick on a segment, or disarms it when
   * passed null. The whole-slot setter the pick begin/set/cancel tools drive — the
   * pick keeps no narrower fields in the store, mirroring the other dialog slices.
   */
  setSegmentSplitPick: (pick: SegmentSplitPick | null) => void;
}

/**
 * segmentSplitInitialState seeds the split-point pick disarmed.
 */
export const segmentSplitInitialState: SegmentSplitSliceState = {
  segmentSplitPick: null,
};

/**
 * createSegmentSplitSlice builds the split-pick action. The single whole-slot
 * setter arms the pick on a segment or clears it, mirroring the share and
 * directions slices' open/close shape.
 */
export const createSegmentSplitSlice: StateCreator<
  SegmentSplitSliceState & SegmentSplitSliceActions,
  [],
  [],
  SegmentSplitSliceActions
> = (set) => ({
  setSegmentSplitPick: (segmentSplitPick) => set({ segmentSplitPick }),
});

/**
 * selectSegmentSplitPick is the armed split-point pick, or null when none is. The
 * narrow selector a map surface reads to decide whether to arm its cross cursor at
 * all, so it re-renders only on the arm/disarm flip.
 */
export const selectSegmentSplitPick = (state: SegmentSplitSliceState): SegmentSplitPick | null =>
  state.segmentSplitPick;
